import React, { useContext, useState, useRef, useEffect, useMemo } from "react";
import useProgramStripeIntegration from "app/utils/hooks/scheduling/useProgramStripeIntegration";
import styles from "../../../routes/ViewProgram/styles.module.scss";
import TrashIco from "app/images/trash.svg";
import { useSelector } from "react-redux";
import { RootState } from "typedefs";
import { RoleType, UserContext } from "app/contexts/UserContext";
import { isEmpty } from "lodash";
import PhotoContainer from "../ImageContainers/PhotoContainer";
import { useTranslation } from "react-i18next";
import DropdownButton from "app/components/DropdownButton";
import { DateTime } from "luxon";
import SquaredColorOption from "../SquaredColorOption";
import { getColorCode } from "app/routes/BuildProgram/Pricing/utils";
import DefaultImage from "../DefaultImage";
import upload from "app/utils/uploader";
import { toast } from "react-toastify";
import { schedulingFetchPatch, schedulingFetchGet } from "app/utils/request/scheduling/fetch";
import EditProgramModal from "app/routes/ManagePrograms/EditProgramModal";
import { ProgramModel } from "app/models/scheduling/ProgramModel";
import { Loading } from "app/components/Wrappers";
import ButtonViewProgram from "../Buttons/ButtonViewProgram";
import RegisterStudentsPopup from "../RegisterStudentsPopup";
import RegistrantsModal from "../RegistrantsModal";
import RegisterAthleteModal from "../RegisterAthleteModal";
import ProgramDetailsModal from "../ProgramDetailsModal";
import { cls } from "app/utils";
import { formatTime, formatFullWeekdayDate } from "app/utils/luxonFormats";
import useCalendarPreferences from "app/utils/hooks/useCalendarPreferences";

const GroupCard = ({
  program,
  multipleDate,
  handleDelete,
  handleMessage,
  eventId,
  event,
  setRefetch,
  isPublic,
  setShowPublicRegisterPopup,
}) => {
  const { t } = useTranslation();
  const userType = useSelector(
    (state: RootState): string => state.user?.data?.kind
  );
  const { preferences } = useCalendarPreferences();
  const isCoach: boolean = userType === RoleType.COACH || userType === RoleType.ADMIN || userType === RoleType.SCHEDULER;
  const date = multipleDate;
  const shortMonth = date.monthShort.toUpperCase();
  const day = date.day;
  const user = useContext(UserContext);
  const isClinic = program?.attendance === 'clinic';
  const timeRange = `${formatTime(event.startDate, preferences.timeFormat)} - ${formatTime(event.endDate, preferences.timeFormat)}`;
  const [eventProgramModel, setEventProgramModel] = useState<ProgramModel>(new ProgramModel(event));
  const [imageUrl, setImageUrl] = useState(event?.externalThumbnailUrl);
  const [newImage, setNewImage] = useState(null);
  const [showDefaultImages, setShowDefaultImages] = useState<boolean>(false);
  const isMountedRef = useRef(true);
  const hasFetchedData = useRef(false);
  const [isEditModalOpen, setIsEditModalOpen] = useState<boolean>(false);
  const [isRegisterAthletesModalOpen, setIsRegisteredAthletesModalOpen] = useState<boolean>(false);
  const [isEventLoading, setIsEventLoading] = useState(false);
  const [showRegisterPopup, setShowRegisterPopup] = useState<boolean>(false);
  const [showProgramDetailsModal, setShowProgramDetailsModal] = useState<boolean>(false);
  const [showRegisterWaitlistPopup, setRegisterWaitlistPopup] = useState<boolean>(false);

  const hasStripeIntegration = useProgramStripeIntegration(eventProgramModel);

  const isRegistered = useMemo(() => {
    if (isCoach || !user?.id || !eventProgramModel) {
      return false;
    }

    return eventProgramModel.allRegistrants.some(
      (registrant) => registrant.studentProfile.id === user.id.toString()
    );
  }, [isCoach, eventProgramModel, user?.id]);

  // CN-1323 (CFTB-3293): the current user's managed dependents who are
  // registered (a dependent's studentProfile.headExternalId is the head's
  // external id). Lets the card tell a parent a family member is registered
  // even when the parent themselves is not.
  const registeredFamilyNames = useMemo(() => {
    if (isCoach || !user?.id || !eventProgramModel) {
      return [];
    }
    return eventProgramModel.allRegistrants
      .filter((registrant) => registrant.studentProfile.headExternalId === user.id.toString())
      .map((registrant) => registrant.studentProfile.firstName);
  }, [isCoach, eventProgramModel, user?.id]);

  useEffect(() => {
    return () => {
      isMountedRef.current = false;
    };
  }, []);

  const prevSpotsBooked = useRef(event?.spotsBooked);

  useEffect(() => {
    if (event) {
      if (!hasFetchedData.current) {
        setEventProgramModel(new ProgramModel(event));
      } else if (
        event.spotsBooked !== undefined &&
        prevSpotsBooked.current !== undefined &&
        event.spotsBooked !== prevSpotsBooked.current
      ) {
        // Spots changed (e.g. after transfer) — refetch full data including registrants
        hasFetchedData.current = false;
        refetchEventProgram();
      }
      prevSpotsBooked.current = event.spotsBooked;
    }
  }, [event, isCoach]);

  useEffect(() => {
    if (!isCoach && !isPublic && eventId) {
      fetchEventProgramIfNeeded();
    }
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  const refetchEventProgram = async () => {
    if (!eventId) { return; }
    try {
      const fetchedProgram = await schedulingFetchGet(`/programs/${eventId}`, {}, {}, null, false);
      setEventProgramModel(new ProgramModel(fetchedProgram));
      hasFetchedData.current = true;
    } catch (error) {
      console.error('Error refetching event program:', error);
    }
  };

  const fetchEventProgramIfNeeded = async () => {
    if (hasFetchedData.current || isEventLoading) {
      return;
    }

    if (!eventId) {
      return;
    }

    try {
      setIsEventLoading(true);
      const fetchedProgram = await schedulingFetchGet(
        `/programs/${eventId}`,
        {},
        {},
        null,
        false
      );

      setEventProgramModel(new ProgramModel(fetchedProgram));
      hasFetchedData.current = true;
    } catch (error) {
      // An athlete who isn't synced into the scheduling service yet gets a 401
      // on the private endpoint; without the public fallback they could never
      // reach the registration popup that performs the sync.
      if (error?.status === 401) {
        try {
          const publicProgram = await schedulingFetchGet(
            `/programs/${eventId}`,
            {},
            {},
            null,
            true
          );

          setEventProgramModel(new ProgramModel(publicProgram));
          hasFetchedData.current = true;
          return;
        } catch (publicError) {
          console.error('Error fetching event program:', publicError);
          toast.error(t("Failed to load program data"));
          return;
        } finally {
          setIsEventLoading(false);
        }
      }
      console.error('Error fetching event program:', error);
      toast.error(t("Failed to load program data"));
    } finally {
      setIsEventLoading(false);
    }
  };

  const isFullWithoutWaitlist =
    eventProgramModel?.spotsBooked >= eventProgramModel?.capacity && !eventProgramModel?.withWaitlist;

  const isDisabled = useMemo(() => {
    if (isCoach) {
      return true;
    }

    const statusCode = eventProgramModel?.registrationStatusCode;
    const status = eventProgramModel?.registrationStatus;

    const hasPendingRegistrations = eventProgramModel?.hasPendingRegistrations || false;

    return (statusCode === "closed" && status === 'Registration Closed') ||
           statusCode === "not_started" ||
           hasPendingRegistrations ||
           isFullWithoutWaitlist;
  }, [isCoach, event, eventProgramModel, user, isFullWithoutWaitlist]);

  const isPassed = useMemo(() => {
    const programEnd: DateTime = DateTime.fromISO(event?.endDate);
    const now: DateTime = DateTime.now();

    return now > programEnd;
  }, [event]);

  const onClickEdit = () => {
    setIsEditModalOpen(true);
  };

  const onClickRegisterAthletes = async () => {
    await fetchEventProgramIfNeeded();
    setIsRegisteredAthletesModalOpen(true);
  };

  const onClickRegistrants = async () => {
    await fetchEventProgramIfNeeded();
    setRegisterWaitlistPopup(true);
  };

  const onClickSendMessage = () => {
    handleMessage?.(eventId);
  };

  const onClickDelete = () => {
    handleDelete(eventId, 'clinic', eventProgramModel?.title, eventProgramModel?.startDate);
  };

  const memberMenu = [
    {
      title: t('Edit'),
      onClick: () => {
        onClickEdit();
      },
      icon: <i className="ico ico-edit-outline" />,
    },
    {
      title: t('Register Athletes'),
      onClick: () => {
        onClickRegisterAthletes();
      },
      icon: <i className="ico ico-user-add-o" />,
    },
    {
      title: t('Registrants and Waitlist'),
      onClick: () => {
        onClickRegistrants();
      },
      icon: <i className="ico ico-grouped-people-outline" />,
    },
    {
      title: t('Send Message'),
      onClick: () => {
        onClickSendMessage();
      },
      icon: <i className="ico ico-dialog" />,
    },
    {
      title: t('Delete'),
      onClick: () => {
        onClickDelete();
      },
      icon: <i className="ico ico-trash-outline" />,
      color: '#E54500',
    },
  ];

  const isActive = (): boolean => {
    const programStart: DateTime = DateTime.fromISO(program?.start);
    const now: DateTime = DateTime.now();

    return now <= programStart;
  };

  const handleURL = async (image) => {
    if (image) {
      setImageUrl(URL.createObjectURL(image));
      setNewImage(image);
    } else {
      setImageUrl(null);
      setNewImage(null);
      try {
        await schedulingFetchPatch(`/programs/${event?.id}/patch`, { external_thumbnail_url: '' });
        toast.success(t("Image removed successfully"));
        setRefetch?.(true);
      } catch (error) {
        console.error("Error removing image:", error);
        toast.error(t("Failed to remove image"));
      }
    }
  };

  const onCropComplete = async (blobURL) => {
    if (!isMountedRef.current) {
      return;
    }

    try {
      const response = await fetch(blobURL);
      const blob = await response.blob();
      const file = new File([blob], "uploaded_image.png", {
        type: "image/png",
      });

      if (!isMountedRef.current) {
        return;
      }

      setNewImage(file);
      setShowDefaultImages(false);

      if (file) {
        const uploadResult = await upload(
          file,
          file.name,
          file.type,
          undefined,
        );

        if (!isMountedRef.current) {
          return;
        }

        setImageUrl(uploadResult.url);
        schedulingFetchPatch(`/programs/${event?.id}/patch`, { external_thumbnail_url: uploadResult.url });
        toast.success(t("Image uploaded successfully"));
      }
    } catch (error) {
      console.error("Error uploading image:", error);
      if (isMountedRef.current) {
        setShowDefaultImages(false);
        toast.error(t("Failed to upload image"));
      }
    }
  };

  const handleRegisterClick = async () => {
    // Only a genuinely public visitor needs an account before going further.
    // This used to send passed sessions down the same path, which meant a
    // signed-in athlete clicking "View Details" on a session they had already
    // attended was asked to create an account. Passed sessions still open the
    // details modal — it is where their own invoice and payment link live —
    // they just cannot be registered for (see isPassed below).
    if (isPublic) {
      setShowPublicRegisterPopup(true);
      return;
    }

    await fetchEventProgramIfNeeded();
    setShowProgramDetailsModal(true);
  };

  const onRegisterAthleteHandle = () => {
    setShowProgramDetailsModal(false);
    setShowRegisterPopup(true);
  };

  const onRegistrantsAthleteHandle = () => {
    setShowProgramDetailsModal(false);
    setRegisterWaitlistPopup(true);
  };

  const renderRegisterButton = () => {
    if (isCoach || !isClinic || (!isRegistered && isPassed)) {
      return null;
    }
    // CN-2612: a full program with no waitlist can't be registered for, but it
    // still has to open. The details modal is the athlete's only way to the
    // registrant list, and to a parent's controls over a family member they
    // registered. Hiding it left the card showing nothing but its status.
    if (isDisabled && !isFullWithoutWaitlist) {
      return null;
    }

    const buttonText = isRegistered || isDisabled ? t("View Details") : t("Register");

    return (
      <div className={cls(styles.buttonsContainer, styles.column_direction)}>
        <ButtonViewProgram
          colorStyle={styles.save_btn}
          text={buttonText}
          handleClick={handleRegisterClick}
        />

        {isRegistered ? (
          <div className={styles.status_container}>
            <i className="ico ico-check-new" />
            <p className={styles.message}>{t("You are registered")}</p>
          </div>
        ) : registeredFamilyNames.length > 0 ? (
          <div className={styles.status_container}>
            <i className="ico ico-check-new" />
            <p className={styles.message}>
              {registeredFamilyNames.length === 1
                ? t("{{name}} is registered", { name: registeredFamilyNames[0] })
                : t("{{count}} family members registered", {
                    count: registeredFamilyNames.length,
                  })}
            </p>
          </div>
        ) : null}
      </div>
    );
  };

  const updateFromRegistrationResponse = (response) => {
    if (!response || !response.id) {
      return;
    }

    setEventProgramModel(new ProgramModel(response));
  };

  const handleRegistrationSuccess = async (response) => {
    if (!response || !response.id) {
      try {
        const updatedProgram = await schedulingFetchGet(`/programs/${eventId}`, {}, {}, null, false);
        updateFromRegistrationResponse(updatedProgram);
      } catch (error) {
        console.error('Error fetching updated program data:', error);
        toast.warning(t("Registration succeeded, but the page could not refresh. Please reload to see updated data."));
      }
      return;
    }

    updateFromRegistrationResponse(response);
  };

  const handleAthleteRegistrationSuccess = async (response) => {
    if (!response || !response.id) {
      try {
        const updatedProgram = await schedulingFetchGet(`/programs/${eventId}`, {}, {}, null, false);
        updateFromRegistrationResponse(updatedProgram);
      } catch (error) {
        console.error('Error fetching updated program data:', error);
        toast.warning(t("Registration succeeded, but the page could not refresh. Please reload to see updated data."));
      }
      return;
    }

    updateFromRegistrationResponse(response);
  };

  const handleRegistrantDeleted = (response) => {
    updateFromRegistrationResponse(response);
  };

  if (isEventLoading) {
    return (
      <div className={styles.groupCard}>
        <Loading loadType="spinner" isLoading />
      </div>
    );
  }

  return (
    <div className={`${styles.groupCard} ${isClinic ? styles.clinic_card : ''}`}>
      {isClinic ? (
        <div className={styles.mobile_row_1}>
          <PhotoContainer
            imageLink={imageUrl}
            type="program_logo"
            allowsDefault
            onSelectClick={() => setShowDefaultImages(true)}
            handleCrop={() => setShowDefaultImages(true)}
            containerStyle={`${styles.details_photo_container} ${!isCoach ? styles.hide_actions : ''}`}
            setUrl={(image) => handleURL(image)}
            uploadedImageContainer={styles.uploaded_image_container}
            allowChange={!isPublic && isCoach}
          />
        </div>
      ) : (
        <>
          <div className={styles.groupCardText}>
            <p className={styles.groupCardMonth}>{shortMonth}</p>
            <h2 className={styles.groupCardDay}>{day}</h2>
          </div>
          <span className={styles.borderVector}></span>
        </>
      )}

      <div className={styles.mobile_row_2_left}>
        <div className={styles.title_date_container}>
          {isClinic && <p className={styles.clinic_title}>{event?.title}</p>}
          <p className={isClinic ? styles.clinic_subtitle : styles.groupCardTime}>
            {formatFullWeekdayDate(event.startDate, preferences.dateFormat)}
          </p>
        </div>

        <p className={isClinic ? styles.clinic_time : styles.groupCardTime2}>{timeRange}</p>
      </div>

      {isClinic && isActive() && isDisabled && (
        <div className={styles.status_wrapper}>
          <SquaredColorOption
            color={getColorCode(eventProgramModel?.registrationStatusCode)}
            text={eventProgramModel?.registrationStatus}
          />
        </div>
      )}

      {isClinic && (
        <div className={styles.mobile_row_2_right}>
          {isCoach && (
            <div className={styles.collect_icons_container}>
              {(event?.collectType === "online" ||
                event?.collectType === "both") && (
                <div className={styles.iconWithTooltip}>
                  <i className="ico ico-online-payment" />
                  <span className={styles.tooltipText}>{t('Collect Online')}</span>
                </div>
              )}
              {(event?.collectType === "offline" ||
                event?.collectType === "both") && (
                <div className={styles.iconWithTooltip}>
                  <i className="ico ico-offline-payment" />
                  <span className={styles.tooltipText}>{t('Collect Offline')}</span>
                </div>
              )}
            </div>
          )}

          {(isCoach || !isDisabled) && !isPassed && (
            <div className={styles.spots_booked_container}>
              <p>{eventProgramModel?.spotsBooked}</p>
              <p>/{eventProgramModel?.capacity}</p>
            </div>
          )}
        </div>
      )}

      {program?.attendance === "series" &&
        !isEmpty(user) &&
        userType !== "athlete" &&
        date > new Date() && (
          <div className={styles.trashIconContainer}>
            <img src={TrashIco} onClick={() => handleDelete(eventId)} />
          </div>
      )}

      {isClinic && isCoach && (
        <DropdownButton
          closeOnClick
          btnContent={<i className="ico ico-options" />}
          btnClassName={styles.options_button}
          hideButtonStyles={true}
          dropdownWrapperClass={styles.options_modal_container}
        >
          {memberMenu.map((item) => (
            <div key={item.title} className={styles.options_item_container}>
              <button onClick={() => item.onClick()} style={{color: item.color}}>
                {item.icon}
                <p>{item.title}</p>
              </button>
            </div>
          ))}
        </DropdownButton>
      )}

      {renderRegisterButton()}

      {showDefaultImages && (
        <DefaultImage
          uploadedImageURL={imageUrl}
          onConfirm={onCropComplete}
          needsCrop={true}
          showModal={() => setShowDefaultImages(prev => !prev)}
          uploadedImage={newImage}
        />
      )}

      <EditProgramModal
        isOpen={isEditModalOpen}
        onClose={() => setIsEditModalOpen(false)}
        eventId={eventId}
        onSuccess={() => setRefetch?.(true)}
      />

      {showRegisterPopup && eventProgramModel && (
        <RegisterStudentsPopup
          programModel={eventProgramModel}
          showModal={setShowRegisterPopup}
        />
      )}

      {showProgramDetailsModal && eventProgramModel && hasFetchedData.current && (
        <ProgramDetailsModal
          isOpen={showProgramDetailsModal}
          onClose={() => setShowProgramDetailsModal(false)}
          programModel={eventProgramModel}
          allowAdditionalPeople={eventProgramModel?.allowAdditionalPeople ?? true}
          isRegistered={isRegistered}
          isPassed={isPassed}
          canRegister={!isDisabled}
          onRegister={onRegisterAthleteHandle}
          onRegistrants={onRegistrantsAthleteHandle}
        />
      )}

      {showRegisterWaitlistPopup && eventProgramModel && (
        <RegistrantsModal
          isOpen={showRegisterWaitlistPopup}
          onClose={() => setRegisterWaitlistPopup(false)}
          programModel={eventProgramModel}
          userType={userType}
          onRegistrationSuccess={handleRegistrationSuccess}
          onRegistrantDeleted={handleRegistrantDeleted}
          title={t('Registrants and Waitlist')}
          onTransferComplete={() => {
            setRegisterWaitlistPopup(false);
            setRefetch?.(true);
          }}
        />
      )}

      {isRegisterAthletesModalOpen && (
        <RegisterAthleteModal
          programModel={eventProgramModel}
          isModalOpen={isRegisterAthletesModalOpen}
          modalHandler={() => setIsRegisteredAthletesModalOpen(false)}
          stripeIntegrationEnabled={hasStripeIntegration}
          onRegistrationSuccess={handleAthleteRegistrationSuccess}
        />
      )}
    </div>
  );
};

export default GroupCard;
